import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import {
  createSessionId,
} from "@/feature/ai-planner/lib/ai-planner-chat.helpers";
import type { AiSuggestedQuery } from "@/feature/ai-planner/types/ai-chat.types";
import type {
  ChatMessage,
  ChatTurn,
} from "@/feature/ai-planner/types/chat-ui.types";
import type { RootState } from "@/redux/store";

type AiPlannerState = {
  sessionId: string;
  turns: ChatTurn[];
  draft: string;
  activeSuggestedQueries: AiSuggestedQuery[];
};

function disableAnimation(message: ChatMessage): ChatMessage {
  return {
    ...message,
    animateText: false,
  };
}

function createInitialState(): AiPlannerState {
  return {
    sessionId: createSessionId(),
    turns: [],
    draft: "",
    activeSuggestedQueries: [],
  };
}

const aiPlannerSlice = createSlice({
  name: "aiPlanner",
  initialState: createInitialState(),
  reducers: {
    setAiPlannerDraft(state, action: PayloadAction<string>) {
      state.draft = action.payload;
    },
    setAiPlannerSuggestedQueries(
      state,
      action: PayloadAction<AiSuggestedQuery[]>,
    ) {
      state.activeSuggestedQueries = action.payload;
    },
    appendAiPlannerTurn(state, action: PayloadAction<ChatTurn>) {
      state.turns.push({
        ...action.payload,
        request: disableAnimation(action.payload.request),
      });
    },
    resolveAiPlannerTurnResponse(
      state,
      action: PayloadAction<{
        turnId: string;
        response: ChatMessage;
      }>,
    ) {
      const turn = state.turns.find(
        (currentTurn) => currentTurn.id === action.payload.turnId,
      );

      if (turn) {
        turn.response = action.payload.response;
      }
    },
    disableAiPlannerMessageAnimation(
      state,
      action: PayloadAction<{ messageId: string }>,
    ) {
      for (const turn of state.turns) {
        if (turn.request.id === action.payload.messageId) {
          turn.request.animateText = false;
          return;
        }

        if (turn.response?.id === action.payload.messageId) {
          turn.response.animateText = false;
          return;
        }
      }
    },
    replaceAiPlannerTurns(state, action: PayloadAction<ChatTurn[]>) {
      state.turns = action.payload;
    },
    resetAiPlannerSession(state) {
      state.sessionId = createSessionId();
      state.turns = [];
      state.draft = "";
      state.activeSuggestedQueries = [];
    },
  },
});

export const {
  appendAiPlannerTurn,
  disableAiPlannerMessageAnimation,
  replaceAiPlannerTurns,
  resetAiPlannerSession,
  resolveAiPlannerTurnResponse,
  setAiPlannerDraft,
  setAiPlannerSuggestedQueries,
} = aiPlannerSlice.actions;

export const selectAiPlannerState = (state: RootState) => state.aiPlanner;

export default aiPlannerSlice;
